/**
 * SQL Server Query Store — defaults, result bounds and value guards shared by
 * the renderer (session deserializer, toolbar, detail pane) and the main
 * process (IPC validation, SQL caps).
 * Kept apart from the contracts in `queryStore.ts`; that module re-exports
 * everything here so existing imports keep working.
 */
import type { QueryStoreMetric } from './queryStore';

export const QUERY_STORE_DEFAULT_PERIOD_HOURS = 24;
export const QUERY_STORE_DEFAULT_MIN_EXECUTIONS = 5;
export const QUERY_STORE_DEFAULT_LIMIT = 50;
export const QUERY_STORE_LIMIT_STEP = 50;
export const QUERY_STORE_METRICS: readonly QueryStoreMetric[] = ['duration', 'cpu_time', 'logical_io_reads'];

/**
 * Result bounds. Every catalog read is capped server-side (`TOP (@cap + 1)`,
 * the extra row only tells whether anything was cut) and the cut is reported
 * with an explicit `…Truncated` flag — never silently.
 */
/** Per-interval rows (interval × plan × execution type) a detail read returns, newest first. */
export const QUERY_STORE_DETAIL_INTERVAL_ROW_CAP = 2000;
/** Plans a detail read lists for one query, most recently compiled first. */
export const QUERY_STORE_DETAIL_PLAN_CAP = 50;
/** Plans per candidate query the comparison aggregates (heaviest by the ranking metric). */
export const QUERY_STORE_COMPARE_PLAN_CAP = 50;
/** Per-interval rows the detail pane renders before "Show more". */
export const QUERY_STORE_DETAIL_INTERVAL_PAGE = 200;

/** Decimal-string guard for catalog ids coming from the renderer. */
export function isCatalogId(value: unknown): value is string {
  return typeof value === 'string' && /^\d{1,20}$/.test(value);
}

/** Numeric order for decimal-string catalog ids (shorter = smaller, then lexical) — never `Number()`. */
export function compareCatalogIds(a: string, b: string): number {
  return a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);
}

export function isQueryStoreMetric(value: unknown): value is QueryStoreMetric {
  return typeof value === 'string' && (QUERY_STORE_METRICS as readonly string[]).includes(value);
}

/**
 * Coerces a persisted or IPC-supplied count (`periodHours`, `minExecutions`,
 * `limit`) to a positive integer; anything else (negative, fractional, string,
 * missing) falls back. Shared by the session deserializer and the IPC boundary.
 */
export function coerceQueryStorePositiveInt(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : fallback;
}
